import React, { useState } from "react";
import { Sliders, RefreshCw, AlertTriangle, Thermometer, Snowflake, Sun } from "lucide-react";
import { api, fmtDec, fmtInt, fmtMWh } from "../lib/api";
import Semaforo from "../components/Semaforo";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
} from "recharts";

const PRESETS = {
  base:        { incremento_climatizacion: 0.05, margen_bateria: 0.10, factor_ocupacion: 1.00, penalizacion_urbano: 0.05, penalizacion_pendiente: 0.02, aplicar_orografia: false, factor_orografia: 0.005, aplicar_climatologia: false, mes_climatologia: null, label: "Base" },
  conservador: { incremento_climatizacion: 0.10, margen_bateria: 0.15, factor_ocupacion: 1.05, penalizacion_urbano: 0.08, penalizacion_pendiente: 0.05, aplicar_orografia: true,  factor_orografia: 0.005, aplicar_climatologia: true,  mes_climatologia: null, label: "Conservador" },
  exigente:    { incremento_climatizacion: 0.18, margen_bateria: 0.20, factor_ocupacion: 1.10, penalizacion_urbano: 0.12, penalizacion_pendiente: 0.10, aplicar_orografia: true,  factor_orografia: 0.008, aplicar_climatologia: true,  mes_climatologia: 1,    label: "Exigente" },
};

const MES_ICON = (mes) => {
  if (["Dic", "Ene", "Feb"].includes(mes)) return <Snowflake size={14} className="text-blue-600" />;
  if (["Jun", "Jul", "Ago"].includes(mes)) return <Sun size={14} className="text-amber-600" />;
  return <Thermometer size={14} className="text-gray-500" />;
};

export default function Escenarios() {
  const [params, setParams] = useState(PRESETS.conservador);
  const [preset, setPreset] = useState("conservador");
  const [result, setResult] = useState(null);
  const [anualClima, setAnualClima] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (k) => {
    setPreset(k);
    setParams({ ...PRESETS[k] });
  };

  const recalc = async () => {
    setLoading(true);
    try {
      const [r, ra] = await Promise.all([
        api.post("/escenario", params),
        params.aplicar_climatologia ? api.post("/escenario/climatologia_anual", params) : Promise.resolve(null),
      ]);
      setResult(r.data);
      setAnualClima(ra ? ra.data : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="escenarios-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Análisis de escenarios</div>
        <h1 className="section-title mt-1">Simulador de consumo</h1>
        <p className="section-sub">Modifica los parámetros y observa el impacto sobre el factor kWh/km, el consumo anual y la clasificación de viabilidad. Incluye climatología histórica del último año.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Controls */}
        <div className="crtm-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
              <Sliders size={16} /> Parámetros del escenario
            </h3>
            <div className="flex gap-1">
              {Object.entries(PRESETS).map(([k, p]) => (
                <button
                  key={k}
                  onClick={() => applyPreset(k)}
                  data-testid={`preset-${k}`}
                  className={
                    "px-3 py-1.5 rounded-md text-xs font-semibold " +
                    (preset === k
                      ? "bg-[#14532D] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200")
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <SliderRow
              label="Incremento por climatización (genérico)"
              hint="HVAC, aire frío/calor (override manual)"
              min={0} max={0.30} step={0.01}
              value={params.incremento_climatizacion}
              onChange={(v) => { setParams({ ...params, incremento_climatizacion: v }); setPreset("custom"); }}
              testId="slider-clima"
              format={(v) => `+${(v * 100).toFixed(0)} %`}
            />
            <SliderRow
              label="Margen de seguridad de batería"
              hint="Reserva operativa"
              min={0} max={0.30} step={0.01}
              value={params.margen_bateria}
              onChange={(v) => { setParams({ ...params, margen_bateria: v }); setPreset("custom"); }}
              testId="slider-bateria"
              format={(v) => `+${(v * 100).toFixed(0)} %`}
            />
            <SliderRow
              label="Factor de ocupación"
              hint="Carga media de pasajeros"
              min={0.85} max={1.20} step={0.01}
              value={params.factor_ocupacion}
              onChange={(v) => { setParams({ ...params, factor_ocupacion: v }); setPreset("custom"); }}
              testId="slider-ocupacion"
              format={(v) => `${v.toFixed(2)} ×`}
            />
            <SliderRow
              label="Penalización urbana"
              hint="Paradas, arranques/frenadas"
              min={0} max={0.20} step={0.01}
              value={params.penalizacion_urbano}
              onChange={(v) => { setParams({ ...params, penalizacion_urbano: v }); setPreset("custom"); }}
              testId="slider-urbano"
              format={(v) => `+${(v * 100).toFixed(0)} %`}
            />
            <SliderRow
              label="Pendiente / condiciones exigentes"
              hint="Penalización genérica adicional"
              min={0} max={0.20} step={0.01}
              value={params.penalizacion_pendiente}
              onChange={(v) => { setParams({ ...params, penalizacion_pendiente: v }); setPreset("custom"); }}
              testId="slider-pendiente"
              format={(v) => `+${(v * 100).toFixed(0)} %`}
            />

            {/* Toggle orografía real */}
            <div className="pt-3 border-t border-gray-100" data-testid="orografia-block">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-semibold text-gray-800">Aplicar orografía real (SRTM DEM)</div>
                  <div className="text-xs text-gray-500">Penaliza el factor según el desnivel acumulado real m/km de cada línea</div>
                </div>
                <input
                  type="checkbox"
                  checked={params.aplicar_orografia}
                  onChange={(e) => { setParams({ ...params, aplicar_orografia: e.target.checked }); setPreset("custom"); }}
                  data-testid="toggle-orografia"
                  className="w-5 h-5 accent-green-900"
                />
              </label>
              {params.aplicar_orografia && (
                <div className="mt-3">
                  <SliderRow
                    label="Factor orografía"
                    hint="Penalización aplicada por cada m/km de ascenso"
                    min={0.002} max={0.020} step={0.001}
                    value={params.factor_orografia}
                    onChange={(v) => { setParams({ ...params, factor_orografia: v }); setPreset("custom"); }}
                    testId="slider-orografia"
                    format={(v) => `+${(v * 100).toFixed(2)} % por m/km`}
                  />
                </div>
              )}
            </div>

            {/* Toggle climatología histórica */}
            <div className="pt-3 border-t border-gray-100" data-testid="climatologia-block">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <Thermometer size={14} className="text-emerald-700" />
                    Aplicar climatología histórica (año de referencia)
                  </div>
                  <div className="text-xs text-gray-500">Penalización HVAC derivada de la temperatura mensual (Open-Meteo ERA5)</div>
                </div>
                <input
                  type="checkbox"
                  checked={params.aplicar_climatologia}
                  onChange={(e) => { setParams({ ...params, aplicar_climatologia: e.target.checked, mes_climatologia: null }); setPreset("custom"); }}
                  data-testid="toggle-climatologia"
                  className="w-5 h-5 accent-green-900"
                />
              </label>

              {params.aplicar_climatologia && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Mes a simular</div>
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => { setParams({ ...params, mes_climatologia: null }); setPreset("custom"); }}
                      data-testid="mes-anual"
                      className={
                        "px-2.5 py-1.5 rounded-md text-xs font-semibold border " +
                        (!params.mes_climatologia
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50")
                      }
                    >
                      Anual (media)
                    </button>
                    {["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((m, i) => {
                      const num = i + 1;
                      const selected = params.mes_climatologia === num;
                      return (
                        <button
                          key={m}
                          onClick={() => { setParams({ ...params, mes_climatologia: num }); setPreset("custom"); }}
                          data-testid={`mes-${m}`}
                          className={
                            "px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 " +
                            (selected
                              ? "bg-emerald-700 text-white border-emerald-700"
                              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50")
                          }
                        >
                          {MES_ICON(m)} {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button onClick={recalc} disabled={loading} className="btn-primary mt-5" data-testid="btn-recalcular">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Calculando…" : "Recalcular escenario"}
          </button>
        </div>

        {/* Result */}
        <div className="crtm-card p-5">
          <h3 className="font-heading font-semibold text-[15px] mb-3">Resultado agregado</h3>
          {!result && (
            <div className="text-sm text-gray-500">
              Pulsa "Recalcular escenario" para ver el impacto sobre la cartera.
            </div>
          )}
          {result && (
            <div className="space-y-3">
              <Stat label="Factor medio original" value={`${fmtDec(result.factor_medio_original, 2)} kWh/km`} />
              <Stat
                label="Factor medio escenario"
                value={`${fmtDec(result.factor_medio_escenario, 2)} kWh/km`}
                delta={(result.factor_medio_escenario - result.factor_medio_original) / result.factor_medio_original}
              />
              <Stat label="Consumo anual original" value={fmtMWh(result.consumo_anual_original)} />
              <Stat
                label="Consumo anual escenario"
                value={fmtMWh(result.consumo_anual_escenario)}
                delta={(result.consumo_anual_escenario - result.consumo_anual_original) / result.consumo_anual_original}
              />

              {result.climatologia?.aplicada && (
                <div className="pt-2 mt-2 border-t border-emerald-100">
                  <div className="kpi-label flex items-center gap-1.5">
                    <Thermometer size={12} className="text-emerald-700" /> Climatología aplicada
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-semibold text-emerald-800">{result.climatologia.etiqueta}</span>{" "}
                    <span className="text-gray-500">→ incremento</span>{" "}
                    <span className="font-semibold text-emerald-800">
                      +{(result.climatologia.factor * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-gray-100 mt-3">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle size={15} />
                  <div className="text-sm font-semibold">
                    {result.n_cambian_categoria} líneas cambian de categoría
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Sobre {result.n_lineas} líneas analizadas.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Variación anual de consumo por climatología */}
      {anualClima && (
        <div className="crtm-card" data-testid="anual-clima-card">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
              <Thermometer size={16} className="text-emerald-700" />
              Variación mensual del consumo por climatología
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Reanálisis histórico Open-Meteo ERA5. Penalización HVAC sobre el factor kWh/km en función de la temperatura media mensual.
              Consumo anual base sin clima: <strong>{fmtMWh(anualClima.consumo_anual_base_kwh)}</strong>{" "}
              → con clima: <strong className="text-emerald-800">{fmtMWh(anualClima.consumo_anual_clima_kwh)}</strong>{" "}
              <span className="text-gray-400">
                (Δ {(((anualClima.consumo_anual_clima_kwh - anualClima.consumo_anual_base_kwh) / anualClima.consumo_anual_base_kwh) * 100).toFixed(1)}%)
              </span>
            </p>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={anualClima.meses}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  label={{ value: "Consumo (MWh)", angle: -90, position: "insideLeft", style: { fill: "#4B5563", fontSize: 12 } }}
                  tickFormatter={(v) => (v / 1000).toFixed(0)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  label={{ value: "Temperatura (°C)", angle: 90, position: "insideRight", style: { fill: "#4B5563", fontSize: 12 } }}
                />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                  formatter={(value, name) => {
                    if (name === "Consumo mensual (MWh)") return [(value / 1000).toFixed(1) + " MWh", name];
                    if (name === "Temperatura media (°C)") return [value.toFixed(1) + " °C", name];
                    return [value, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "#4B5563" }} iconType="circle" />
                <Bar yAxisId="left" dataKey="consumo_mes_kwh" name="Consumo mensual (MWh)" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Line yAxisId="right" type="monotone" dataKey="temp_media" name="Temperatura media (°C)" stroke="#B45309" strokeWidth={2.5} dot={{ r: 4, fill: "#B45309" }} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Tabla resumen */}
            <div className="overflow-x-auto mt-5">
              <table className="crtm-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="text-right">T media (°C)</th>
                    <th className="text-right">Incremento HVAC</th>
                    <th className="text-right">Factor kWh/km</th>
                    <th className="text-right">Consumo mes</th>
                  </tr>
                </thead>
                <tbody>
                  {anualClima.meses.map((m) => (
                    <tr key={m.mes_num} data-testid={`fila-mes-${m.mes}`}>
                      <td className="font-semibold flex items-center gap-2">{MES_ICON(m.mes)} {m.mes}</td>
                      <td className="text-right font-mono">{m.temp_media.toFixed(1)}</td>
                      <td className="text-right font-mono">
                        <span className={m.incremento_pct > 0 ? "text-emerald-800 font-semibold" : "text-gray-400"}>
                          +{m.incremento_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="text-right font-mono">{fmtDec(m.factor_medio_kwh_km, 2)}</td>
                      <td className="text-right font-mono">{fmtMWh(m.consumo_mes_kwh)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {result && (
        <>
          {/* Cambios de categoría */}
          <div className="crtm-card">
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-heading font-semibold text-[15px]">Líneas que cambian de categoría</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="crtm-table">
                <thead>
                  <tr>
                    <th>Línea</th>
                    <th>Lote</th>
                    <th>Tamaño</th>
                    <th>kWh/km base → escenario</th>
                    <th>Consumo anual base → escenario</th>
                    <th>Viabilidad base</th>
                    <th>Viabilidad escenario</th>
                  </tr>
                </thead>
                <tbody>
                  {result.lineas.filter((l) => l.cambia).slice(0, 30).map((l) => (
                    <tr key={l.codigo_linea} data-testid={`escenario-row-${l.codigo_linea}`}>
                      <td>
                        <div className="font-semibold">{l.numero_linea}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{l.denominacion}</div>
                      </td>
                      <td><span className="pill neutral">{l.lote}</span></td>
                      <td className="text-xs">{l.tamano_vehiculo}</td>
                      <td className="font-mono">
                        {fmtDec(l.factor_original, 2)} → <strong>{fmtDec(l.factor_escenario, 2)}</strong>
                      </td>
                      <td className="font-mono">
                        {fmtMWh(l.consumo_anual_original)} → <strong>{fmtMWh(l.consumo_anual_escenario)}</strong>
                      </td>
                      <td><Semaforo viabilidad={l.viabilidad_original} /></td>
                      <td><Semaforo viabilidad={l.viabilidad_escenario} /></td>
                    </tr>
                  ))}
                  {result.lineas.filter((l) => l.cambia).length === 0 && (
                    <tr><td colSpan={7} className="text-center text-gray-400 py-6">Ninguna línea cambia de categoría con estos parámetros.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SliderRow = ({ label, hint, min, max, step, value, onChange, format, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between mb-1">
      <div>
        <div className="text-sm font-semibold text-gray-800">{label}</div>
        <div className="text-xs text-gray-500">{hint}</div>
      </div>
      <div className="font-mono text-sm font-bold text-[#14532D]">{format(value)}</div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-green-900"
    />
  </div>
);

const Stat = ({ label, value, delta }) => (
  <div className="border-b border-gray-100 last:border-0 pb-2 last:pb-0">
    <div className="kpi-label">{label}</div>
    <div className="flex items-center gap-2 mt-0.5">
      <div className="font-semibold text-gray-900">{value}</div>
      {delta !== undefined && delta !== 0 && (
        <span
          className={`text-xs font-semibold ${delta > 0 ? "text-red-700" : "text-green-700"}`}
        >
          {delta > 0 ? "+" : ""}
          {(delta * 100).toFixed(1)}%
        </span>
      )}
    </div>
  </div>
);
